import React, { useEffect, useState, useCallback } from 'react';
import { telnyxNumbersApi, telnyxConfigApi } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { Plus, Trash2, Phone, Edit2, Settings } from 'lucide-react';
import { toast } from 'sonner';

const ROUTING_LABELS = {
  direct: 'Direct',
  ring_group: 'Ring Group',
  ivr: 'IVR Menu',
};


export default function TelephonyTab({ exerciseId, pbxNumbers = [] }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgs, allNumbers] = await Promise.all([
        telnyxConfigApi.listForExercise(exerciseId),
        telnyxNumbersApi.list(),
      ]);
      setConfigs(cfgs || []);
      // Filter to numbers not already assigned to this exercise
      const assignedIds = new Set((cfgs || []).map((c) => c.phone_number_id));
      setAvailableNumbers((allNumbers || []).filter((n) => !assignedIds.has(n.phone_number_id)));
    } catch (e) {
      toast.error(`Failed to load telephony config: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => { if (exerciseId) load(); }, [exerciseId, load]);

  const handleRemove = async (cfg) => {
    if (!window.confirm(`Unassign ${cfg.phone_number} (${cfg.label || ROUTING_LABELS[cfg.routing_mode]}) from this exercise?`)) return;
    try {
      await telnyxConfigApi.remove(cfg.config_id);
      toast.success('Phone number unassigned');
      load();
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    }
  };

  return (
    <div className="space-y-4" data-testid="telephony-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Phone className="w-5 h-5" /> Telephony Numbers
          </h3>
          <p className="text-xs text-muted-foreground">
            Assign one or more phone numbers to this exercise. Each number can have its own IVR / Direct / Ring Group routing.
          </p>
        </div>
        <Button
          onClick={() => setAssignOpen(true)}
          data-testid="assign-number-btn"
          disabled={availableNumbers.length === 0}
          title={availableNumbers.length === 0 ? 'No available numbers — add one in Hub → Phone Numbers, or remove an existing assignment first' : 'Assign a phone number to this exercise'}
        >
          <Plus className="w-4 h-4 mr-2" />
          Assign Number
        </Button>
      </div>

      {availableNumbers.length === 0 && configs.length === 0 && !loading && (
        <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-2.5">
          No phone numbers exist yet. Add one in <span className="font-medium">Hub → Phone Numbers</span> first.
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-muted-foreground text-sm">Loading...</div>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Phone className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground">No phone numbers assigned to this exercise yet.</p>
            {availableNumbers.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {availableNumbers.length} number{availableNumbers.length !== 1 ? 's' : ''} available — click "Assign Number" above.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {configs.map((cfg) => (
            <Card key={cfg.config_id} data-testid={`telephony-config-${cfg.config_id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-medium">{cfg.phone_number}</span>
                    <Badge variant="secondary">{ROUTING_LABELS[cfg.routing_mode] || cfg.routing_mode}</Badge>
                    {cfg.outbound_caller_id_enabled && <Badge variant="outline" className="text-xs">Outbound Caller ID</Badge>}
                  </div>
                  <p className="text-sm font-medium mt-1">{cfg.label || cfg.phone_number_label || '—'}</p>
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {cfg.routing_mode === 'direct' && cfg.direct_extension && (
                      <p>→ Direct ring: ext {cfg.direct_extension}</p>
                    )}
                    {cfg.routing_mode === 'ring_group' && (cfg.ring_group_extensions || []).length > 0 && (
                      <p>→ {cfg.ring_group_strategy} ring: {cfg.ring_group_extensions.join(', ')}</p>
                    )}
                    {cfg.routing_mode === 'ivr' && cfg.ivr_menu && (
                      <p>→ IVR menu: {(cfg.ivr_menu.options || []).length} option{(cfg.ivr_menu.options || []).length !== 1 ? 's' : ''}</p>
                    )}
                    {cfg.greeting_text && <p className="line-clamp-1">📢 "{cfg.greeting_text}"</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEditingConfig(cfg)} data-testid={`edit-cfg-${cfg.config_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleRemove(cfg)} data-testid={`remove-cfg-${cfg.config_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {assignOpen && (
        <TelephonyConfigDialog
          mode="create"
          exerciseId={exerciseId}
          availableNumbers={availableNumbers}
          pbxNumbers={pbxNumbers}
          onClose={() => setAssignOpen(false)}
          onSaved={() => { setAssignOpen(false); load(); }}
        />
      )}
      {editingConfig && (
        <TelephonyConfigDialog
          mode="edit"
          exerciseId={exerciseId}
          existing={editingConfig}
          pbxNumbers={pbxNumbers}
          onClose={() => setEditingConfig(null)}
          onSaved={() => { setEditingConfig(null); load(); }}
        />
      )}
    </div>
  );
}


function TelephonyConfigDialog({ mode, exerciseId, existing, availableNumbers, pbxNumbers = [], onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [phoneNumberId, setPhoneNumberId] = useState(existing?.phone_number_id || (availableNumbers?.[0]?.phone_number_id || ''));
  const [label, setLabel] = useState(existing?.label || '');
  const [routingMode, setRoutingMode] = useState(existing?.routing_mode || 'direct');
  const [directExt, setDirectExt] = useState(existing?.direct_extension || '');
  const [ringGroupExt, setRingGroupExt] = useState((existing?.ring_group_extensions || []).join(','));
  const [ringStrategy, setRingStrategy] = useState(existing?.ring_group_strategy || 'simultaneous');
  const [greetingText, setGreetingText] = useState(existing?.greeting_text || '');
  const [outboundCid, setOutboundCid] = useState(existing?.outbound_caller_id_enabled || false);
  const [ivrGreeting, setIvrGreeting] = useState(existing?.ivr_menu?.greeting_text || '');
  const [ivrMenuText, setIvrMenuText] = useState(existing?.ivr_menu?.menu_text || 'Press 1 for the first option, 2 for the second.');
  const [ivrOptions, setIvrOptions] = useState(existing?.ivr_menu?.options || [{ digit: '1', label: '', action: 'extension', extension: '' }]);
  const [saving, setSaving] = useState(false);

  const updateOption = (idx, patch) => {
    setIvrOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };
  const addOption = () => setIvrOptions((prev) => [...prev, { digit: String(prev.length + 1), label: '', action: 'extension', extension: '' }]);
  const removeOption = (idx) => setIvrOptions((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!isEdit && !phoneNumberId) { toast.error('Select a phone number'); return; }

    // Cross-validate against actual PBX extensions for this exercise
    const validExtSet = new Set((pbxNumbers || []).map((n) => String(n.extension)));
    const validateExt = (ext, fieldLabel) => {
      if (validExtSet.size === 0) return true; // no PBX numbers loaded — backend will validate
      if (!validExtSet.has(String(ext))) {
        toast.error(`${fieldLabel} "${ext}" doesn't match any PBX extension in this exercise. Available: ${[...validExtSet].sort().join(', ') || 'none'}`);
        return false;
      }
      return true;
    };

    if (routingMode === 'direct') {
      if (!directExt) { toast.error('Direct extension required'); return; }
      if (!validateExt(directExt, 'Direct extension')) return;
    }
    if (routingMode === 'ring_group') {
      if (!ringGroupExt.trim()) { toast.error('Ring group extensions required'); return; }
      const exts = ringGroupExt.split(',').map((e) => e.trim()).filter(Boolean);
      for (const e of exts) {
        if (!validateExt(e, 'Ring group extension')) return;
      }
    }
    if (routingMode === 'ivr') {
      if (ivrOptions.length === 0) { toast.error('Add at least one IVR option'); return; }
      for (const opt of ivrOptions) {
        if (opt.action === 'extension') {
          if (!opt.extension) { toast.error(`IVR option ${opt.digit}: extension required`); return; }
          if (!validateExt(opt.extension, `IVR option ${opt.digit} extension`)) return;
        }
        if (opt.action === 'ring_group') {
          for (const e of (opt.ring_group || [])) {
            if (!validateExt(e, `IVR option ${opt.digit} ring group extension`)) return;
          }
        }
      }
    }

    const payload = {
      label: label.trim(),
      routing_mode: routingMode,
      direct_extension: routingMode === 'direct' ? directExt : null,
      ring_group_extensions: routingMode === 'ring_group' ? ringGroupExt.split(',').map((e) => e.trim()).filter(Boolean) : null,
      ring_group_strategy: ringStrategy,
      greeting_text: greetingText,
      outbound_caller_id_enabled: outboundCid,
      ivr_menu: routingMode === 'ivr' ? {
        greeting_text: ivrGreeting,
        menu_text: ivrMenuText,
        options: ivrOptions,
        timeout_action: 'hangup',
        invalid_action: 'repeat',
      } : null,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await telnyxConfigApi.update(existing.config_id, payload);
        toast.success('Telephony config updated');
      } else {
        await telnyxConfigApi.create({ ...payload, exercise_id: exerciseId, phone_number_id: phoneNumberId });
        toast.success('Phone number assigned');
      }
      onSaved();
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="telephony-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {isEdit ? 'Edit Number Routing' : 'Assign Phone Number'}
          </DialogTitle>
          <DialogDescription>
            Configure how inbound calls to this number are routed within the exercise.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEdit && (
            <div>
              <Label>Phone Number</Label>
              <Select value={phoneNumberId} onValueChange={setPhoneNumberId}>
                <SelectTrigger data-testid="cfg-phone-number-select"><SelectValue placeholder="Select a number..." /></SelectTrigger>
                <SelectContent>
                  {availableNumbers.map((n) => (
                    <SelectItem key={n.phone_number_id} value={n.phone_number_id}>
                      {n.phone_number} {n.label ? `— ${n.label}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Label / Role (e.g. "Coast Guard SimCell")</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Coast Guard SimCell" data-testid="cfg-label-input" />
          </div>

          <div>
            <Label>Routing Mode</Label>
            <Select value={routingMode} onValueChange={setRoutingMode}>
              <SelectTrigger data-testid="cfg-routing-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Direct — ring a single extension</SelectItem>
                <SelectItem value="ring_group">Ring Group — ring multiple extensions</SelectItem>
                <SelectItem value="ivr">IVR Menu — branch on caller's digit choice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Greeting Text (TTS — plays before routing)</Label>
            <Textarea
              rows={2}
              value={greetingText}
              onChange={(e) => setGreetingText(e.target.value)}
              placeholder="You have reached the Coast Guard simulation cell."
              data-testid="cfg-greeting-input"
            />
          </div>

          {routingMode === 'direct' && (
            <div>
              <Label>Direct Extension</Label>
              <Select value={directExt} onValueChange={setDirectExt}>
                <SelectTrigger data-testid="cfg-direct-ext-select"><SelectValue placeholder="Select extension..." /></SelectTrigger>
                <SelectContent>
                  {pbxNumbers.map((n) => (
                    <SelectItem key={n.extension} value={n.extension}>
                      {n.extension} {n.role_name ? `— ${n.role_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pbxNumbers.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No PBX extensions exist for this exercise yet — set them up in the Phone tab first.</p>
              )}
            </div>
          )}

          {routingMode === 'ring_group' && (
            <div className="space-y-2">
              <div>
                <Label>Ring Group Extensions</Label>
                {pbxNumbers.length > 0 ? (
                  <div className="space-y-1.5 border rounded-md p-2 max-h-44 overflow-y-auto bg-background" data-testid="cfg-ring-group-checklist">
                    {pbxNumbers.map((n) => {
                      const cur = ringGroupExt.split(',').map((s) => s.trim()).filter(Boolean);
                      const checked = cur.includes(String(n.extension));
                      return (
                        <label key={n.extension} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 px-1.5 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...cur, String(n.extension)]
                                : cur.filter((x) => x !== String(n.extension));
                              setRingGroupExt(next.join(','));
                            }}
                          />
                          <span className="font-mono w-12">{n.extension}</span>
                          <span className="text-muted-foreground text-xs">{n.role_name || '—'}</span>
                          {n.assigned_user_id ? null : <span className="text-amber-600 text-xs">(unassigned)</span>}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <Input
                    value={ringGroupExt}
                    onChange={(e) => setRingGroupExt(e.target.value)}
                    placeholder="1001, 1002, 1003"
                    data-testid="cfg-ring-group-input"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Selected: {ringGroupExt.split(',').map((s) => s.trim()).filter(Boolean).join(', ') || 'none'}
                </p>
              </div>
              <div>
                <Label>Strategy</Label>
                <Select value={ringStrategy} onValueChange={setRingStrategy}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simultaneous">Ring all simultaneously</SelectItem>
                    <SelectItem value="sequential">Ring sequentially</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {routingMode === 'ivr' && (
            <div className="space-y-3 border rounded-md p-3 bg-muted/30">
              <div>
                <Label>IVR Greeting</Label>
                <Textarea rows={2} value={ivrGreeting} onChange={(e) => setIvrGreeting(e.target.value)} placeholder="Welcome to the Crisis Response Centre." />
              </div>
              <div>
                <Label>Menu Prompt</Label>
                <Textarea
                  rows={2}
                  value={ivrMenuText}
                  onChange={(e) => setIvrMenuText(e.target.value)}
                  placeholder="Press 1 for Coast Guard, 2 for Port Authority, 3 for Media."
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Options</Label>
                  <Button size="sm" variant="outline" onClick={addOption}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Option
                  </Button>
                </div>
                {ivrOptions.map((opt, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`ivr-option-${idx}`}>
                    <div className="col-span-1">
                      <Label className="text-xs">Digit</Label>
                      <Input value={opt.digit} onChange={(e) => updateOption(idx, { digit: e.target.value })} maxLength={1} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Label</Label>
                      <Input value={opt.label} onChange={(e) => updateOption(idx, { label: e.target.value })} placeholder="Coast Guard" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Action</Label>
                      <Select value={opt.action} onValueChange={(v) => updateOption(idx, { action: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="extension">Ring extension</SelectItem>
                          <SelectItem value="ring_group">Ring group</SelectItem>
                          <SelectItem value="hangup">Hang up</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Target</Label>
                      {opt.action === 'extension' ? (
                        pbxNumbers.length > 0 ? (
                          <Select value={opt.extension || ''} onValueChange={(v) => updateOption(idx, { extension: v })}>
                            <SelectTrigger data-testid={`ivr-option-${idx}-ext-select`}><SelectValue placeholder="Select ext..." /></SelectTrigger>
                            <SelectContent>
                              {pbxNumbers.map((n) => (
                                <SelectItem key={n.extension} value={String(n.extension)}>
                                  {n.extension} {n.role_name ? `— ${n.role_name}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={opt.extension || ''} onChange={(e) => updateOption(idx, { extension: e.target.value })} placeholder="1001" />
                        )
                      ) : opt.action === 'ring_group' ? (
                        <Input value={(opt.ring_group || []).join(',')} onChange={(e) => updateOption(idx, { ring_group: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="1001,1002" />
                      ) : (
                        <Input disabled value="—" />
                      )}
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" onClick={() => removeOption(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="outbound-cid"
              checked={outboundCid}
              onChange={(e) => setOutboundCid(e.target.checked)}
              data-testid="cfg-outbound-cid-checkbox"
            />
            <Label htmlFor="outbound-cid" className="text-sm">
              Allow this number as outbound caller ID
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="cfg-save-btn">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Assign Number')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
